import type { Filter } from 'contexts/preferences/listing-params/types';
import { AlertFrequency } from '../value-objects/alert-frequency';
import { asMapBoundedSearch } from '../filter-geography';
import { SavedSearchPolicy } from '../policies/saved-search-policy';
import type {
  DraftValidationResult,
  MapBounds,
  SavedSearchCreateAttributes,
  SavedSearchFilterParams,
} from '../types';

/** Last-resort description when no name is supplied and no generator is injected. */
const DEFAULT_DESCRIPTION = 'My Saved Search';

/**
 * Returns a new filter with price/sqft maxes cleared when they fall below their
 * corresponding min. Mirrors the modal's `validateRangers`. Kept private to the
 * draft: it is a normalization step applied when composing a new search, not a
 * policy decision.
 */
function normalizeRanges(filter: Filter): Filter {
  const next: Filter = { ...filter };
  if (next.priceMin != null && next.priceMax != null && next.priceMin > next.priceMax) {
    next.priceMax = null;
  }
  if (next.sqftMin != null && next.sqftMax != null && next.sqftMin > next.sqftMax) {
    next.sqftMax = null;
  }
  return next;
}

export interface SavedSearchDraftInput {
  filter: Filter;
  source: string;
  /** User-supplied name; wins over the generated description. */
  name?: string;
  /** Requested alert frequency; clamped to what the filter status permits. */
  frequency?: AlertFrequency;
  /**
   * The map's current rectangle. Recorded as the `bbox` filter param when the
   * search is located by the map — either because it has no area slug, or
   * because `locatedByMap` says the page locates searches that way. Ignored for
   * a search located by an area, because the slug already locates it and
   * client-app would ignore a box beside one.
   */
  bounds?: MapBounds | null;
  /**
   * Whether the page saving this search locates searches by the map rather than
   * by an area. True on the search page, where the map is the search.
   *
   * Such a search must not keep a slug. `listingParams` survives soft
   * navigation, and searching a location on the search page sets one, so a slug
   * for a previous area can be sitting in the filter. client-app's
   * `SavedSearchFilter#geo` returns `{ area_slug: }` and never looks at `bbox`
   * when a slug is present, so leaving it there does not add noise — it decides
   * the search, silently covering a whole city instead of the viewport the user
   * framed. See `asMapBoundedSearch`.
   */
  locatedByMap?: boolean;
  /**
   * Injected, impure description generator (e.g. `generateSearchDescriptionFromFilters`).
   * Kept out of the pure domain and passed in from the hook/UI.
   */
  generateDescription?: (filter: Filter) => string;
}

/**
 * SavedSearchDraft
 *
 * Factory for the unpersisted "save current search" case. `create` composes a
 * candidate from the raw filter — clearing invalid ranges, resolving the
 * description (explicit name › generated › default), and clamping the alert
 * frequency to what the status allows — then `validate` checks it and
 * `toCreatePayload` emits the create request body.
 *
 * Distinct from `SavedSearchRecord`: it has no identity yet and maps to the
 * create (not update) request, and — unlike a record — it is never edited after
 * construction, so it exposes no getters or `with*` mutators. Construct via
 * `SavedSearchDraft.create`.
 */
export class SavedSearchDraft {
  readonly #filter: SavedSearchFilterParams;
  readonly #description: string;
  readonly #frequency: AlertFrequency;
  readonly #source: string;

  private constructor(
    filter: SavedSearchFilterParams,
    description: string,
    frequency: AlertFrequency,
    source: string
  ) {
    this.#filter = filter;
    this.#description = description;
    this.#frequency = frequency;
    this.#source = source;
  }

  static create({
    filter,
    source,
    name,
    frequency,
    bounds,
    locatedByMap,
    generateDescription,
  }: SavedSearchDraftInput): SavedSearchDraft {
    // A search with no area of its own is located by the map, and the search
    // page locates searches by the map even when a slug is sitting in the
    // filter. Either way the map's rectangle becomes the record of where the
    // search applies, and any slug or inherited boundary is dropped so it
    // cannot decide the search instead.
    const ranged = normalizeRanges(filter);
    const isMapLocated = locatedByMap || SavedSearchPolicy.requiresBoundingBox(ranged);
    const normalizedFilter = isMapLocated ? asMapBoundedSearch(ranged, bounds) : ranged;
    const description = SavedSearchDraft.resolveDescription(normalizedFilter, name, generateDescription);
    const requested = frequency ?? AlertFrequency.DEFAULT;
    const resolvedFrequency = SavedSearchPolicy.resolveFrequency(normalizedFilter.status, requested);
    return new SavedSearchDraft(normalizedFilter, description, resolvedFrequency, source);
  }

  /** The map rectangle recorded on this draft, or null when none was needed. */
  get boundingBox(): string | null {
    return this.#filter.bbox ?? null;
  }

  private static resolveDescription(
    filter: Filter,
    name: string | undefined,
    generateDescription: ((filter: Filter) => string) | undefined
  ): string {
    const trimmedName = name?.trim();
    if (trimmedName) {
      return trimmedName;
    }
    const generated = generateDescription?.(filter)?.trim();
    return generated || DEFAULT_DESCRIPTION;
  }

  validate(): DraftValidationResult {
    return SavedSearchPolicy.validateDraft(this.#filter);
  }

  toCreatePayload(): SavedSearchCreateAttributes {
    return {
      description: this.#description,
      frequency: this.#frequency.seconds,
      source: this.#source,
      formParams: { filter: this.#filter },
    };
  }
}
